/**
 * Shared-device ("kiosk") sessions: the pure rules, no React and no storage.
 *
 * A shop-floor PC signs in once as a generic device account, so the session
 * says nothing about who is standing at it. The person types their name (and
 * optionally how to reach them) before anything else, and every API request
 * carries both as headers so the ticket names a human (1 Oct).
 *
 * Kept free of imports so `node --test` can load it directly.
 */

/** Request headers the backend reads the kiosk person from. */
export const KIOSK_NAME_HEADER = "X-Kiosk-Name";
export const KIOSK_CONTACT_HEADER = "X-Kiosk-Contact";

/** Name length bounds, after trimming. Mirrors the backend's check. */
export const KIOSK_NAME_MIN_LENGTH = 2;
export const KIOSK_NAME_MAX_LENGTH = 80;
/** Contact is optional; this is its upper bound after trimming. */
export const KIOSK_CONTACT_MAX_LENGTH = 120;

/** 422 when a ticket is filed on a shared device without a name. */
export const KIOSK_NAME_REQUIRED_CODE = "KIOSK_NAME_REQUIRED";
/** 403 for an action a shared device may not take (raising for a colleague…). */
export const KIOSK_NOT_ALLOWED_CODE = "KIOSK_NOT_ALLOWED";

/** Who is using the shared device right now. */
export interface KioskPerson {
  name: string;
  /** Phone, extension or a supervisor's name; empty when not given. */
  contact: string;
}

export type KioskNameProblem = "required" | "tooShort" | "tooLong";
export type KioskContactProblem = "tooLong";

/** Why a typed name cannot be used, or null when it can. */
export function validateKioskName(raw: string): KioskNameProblem | null {
  const name = raw.trim();
  if (name === "") {
    return "required";
  }
  if (name.length < KIOSK_NAME_MIN_LENGTH) {
    return "tooShort";
  }
  if (name.length > KIOSK_NAME_MAX_LENGTH) {
    return "tooLong";
  }
  return null;
}

/** Why a typed contact cannot be used, or null when it can (blank is fine). */
export function validateKioskContact(raw: string): KioskContactProblem | null {
  return raw.trim().length > KIOSK_CONTACT_MAX_LENGTH ? "tooLong" : null;
}

/**
 * The stored person from its serialised form, or null.
 *
 * sessionStorage is user-editable, so anything that would not pass the step's
 * own validation is treated as "nobody yet" rather than sent to the server.
 */
export function parseKioskPerson(raw: string | null): KioskPerson | null {
  if (raw === null) {
    return null;
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) {
      return null;
    }
    const { name, contact } = value as { name?: unknown; contact?: unknown };
    if (typeof name !== "string" || validateKioskName(name) !== null) {
      return null;
    }
    const safeContact = typeof contact === "string" ? contact.trim() : "";
    if (validateKioskContact(safeContact) !== null) {
      return null;
    }
    return { name: name.trim(), contact: safeContact };
  } catch {
    return null;
  }
}

/**
 * The headers for one request.
 *
 * Empty when nobody has entered a name, so a normal (non-kiosk) session sends
 * exactly what it always did. Values are `encodeURIComponent`-encoded: header
 * values must be ISO-8859-1, and names like "Łukasz" or "张伟" are not.
 */
export function kioskHeaders(person: KioskPerson | null): Record<string, string> {
  if (person === null || person.name.trim() === "") {
    return {};
  }
  const headers: Record<string, string> = {
    [KIOSK_NAME_HEADER]: encodeURIComponent(person.name.trim()),
  };
  const contact = person.contact.trim();
  if (contact !== "") {
    headers[KIOSK_CONTACT_HEADER] = encodeURIComponent(contact);
  }
  return headers;
}

/** Whether an API error body is the server asking for the kiosk name. */
export function isKioskNameRequired(body: unknown): boolean {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { code?: unknown }).code === KIOSK_NAME_REQUIRED_CODE
  );
}

// ─── Idle sign-out ─────────────────────────────────────────────────────────

/** No input for this long on a shared device brings up "Still there?". */
export const KIOSK_IDLE_MS = 15 * 60 * 1000;
/** How long "Still there?" waits before signing the device out. */
export const KIOSK_WARNING_MS = 60 * 1000;

export type KioskIdlePhase = "active" | "warning" | "expired";

export interface KioskIdleInput {
  now: number;
  /** Last pointer/key/touch/wheel input. */
  lastActivityAt: number;
  /** When "Still there?" opened; null while it is closed. */
  warningStartedAt: number | null;
  idleMs?: number;
  warningMs?: number;
}

export interface KioskIdleState {
  phase: KioskIdlePhase;
  /** Whole seconds left on the warning; 0 outside it. */
  secondsLeft: number;
}

/**
 * Where the idle timer stands.
 *
 * Once the warning is open, activity no longer matters: only its buttons
 * close it. A stray touch from the next person in line must not silently
 * keep the previous person's session alive.
 */
export function kioskIdleState({
  now,
  lastActivityAt,
  warningStartedAt,
  idleMs = KIOSK_IDLE_MS,
  warningMs = KIOSK_WARNING_MS,
}: KioskIdleInput): KioskIdleState {
  if (warningStartedAt === null) {
    if (now - lastActivityAt < idleMs) {
      return { phase: "active", secondsLeft: 0 };
    }
    return { phase: "warning", secondsLeft: Math.ceil(warningMs / 1000) };
  }
  const remaining = warningMs - (now - warningStartedAt);
  if (remaining <= 0) {
    return { phase: "expired", secondsLeft: 0 };
  }
  return { phase: "warning", secondsLeft: Math.ceil(remaining / 1000) };
}
